import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { Cart } from '~/graphql/graphql';
import { applyQuantityOverridesToItems } from '~/redux/reducers/cart';
import type { RootState } from '~/redux/store';

/**
 * Carts as they should be shown: the caller's server carts with any in-flight
 * quantity edit merged over them. Summary totals read through this so they agree
 * with the row the buyer is editing.
 */
export function useCartsWithQuantityOverrides(carts: Cart[]): Cart[] {
  const overrides = useSelector(
    (state: RootState) => state.cart.quantityOverrides
  );

  return useMemo(
    () =>
      carts.map((cart) => {
        const forCart = overrides[cart.id];
        if (!forCart || !cart.items) return cart;
        return {
          ...cart,
          items: applyQuantityOverridesToItems(cart.items, forCart),
        };
      }),
    [carts, overrides]
  );
}

type OverridableItem = {
  platformProductsLengths?: { id?: string | null } | null;
  quantity: { value: number };
};

/**
 * The merge for a single row, so a row shows its own pending edit whatever its
 * parent handed down. Merging is idempotent, so this composes with a parent that
 * already merged.
 */
export function useItemWithQuantityOverride<T extends OverridableItem>(
  cartId: string,
  item: T
): T {
  const overrides = useSelector(
    (state: RootState) => state.cart.quantityOverrides[cartId]
  );

  return useMemo(
    () => applyQuantityOverridesToItems([item], overrides)[0],
    [item, overrides]
  );
}

/**
 * The same merge for a surface that already holds its own server items — the
 * order builder reads those from the cart query rather than from Redux.
 */
export function useItemsWithQuantityOverrides<T extends OverridableItem>(
  cartId: string,
  items: T[]
): T[] {
  const overrides = useSelector(
    (state: RootState) => state.cart.quantityOverrides[cartId]
  );

  return useMemo(
    () => applyQuantityOverridesToItems(items, overrides),
    [items, overrides]
  );
}
